import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiTrash2, FiEye, FiEyeOff, FiCopy, FiPower, FiCode, FiPlus, FiX, FiKey, FiAlertTriangle, FiCheck, FiInfo } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Sidebar from "../Sidebar/Sidebar";
import { SmsApis } from "../../apis/smsApis";
import AdminNavbar from "../Navbars/AdminNavbar.js";

const inputCls = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all";
const labelCls = "block text-xs font-bold text-gray-700 mb-1.5";

function Modal({ open, onClose, children, width = "max-w-lg" }: {
  open: boolean; onClose: () => void; children: React.ReactNode; width?: string;
}) {
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-3xl shadow-2xl w-full ${width} max-h-[92vh] flex flex-col`}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, subtitle, onClose }: { title: string; subtitle?: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
      <div>
        <h2 className="text-base font-extrabold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 text-gray-400 hover:bg-gray-200 transition-colors">
        <FiX size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

const CODE_SNIPPETS = {
  php: `<?php
function formatPhoneNumber($phoneNumber) {
    $phoneNumber = preg_replace('/[^0-9]/', '', $phoneNumber);
    if (substr($phoneNumber, 0, 3) !== '234' && substr($phoneNumber, 0, 1) === '0') {
        $phoneNumber = '234' . substr($phoneNumber, 1);
    } elseif (substr($phoneNumber, 0, 3) !== '234') {
        $phoneNumber = '234' . $phoneNumber;
    }
    return $phoneNumber;
}

$apiKey    = 'YOUR_API_KEY_HERE';
$senderId  = 'YOUR_SENDER_ID';
$payload   = [
    'sender_id'  => $senderId,
    'recipients' => [formatPhoneNumber('08012345678')],
    'message'    => 'Your OTP is: 123456. Valid for 60 seconds.',
];

$response = Http::withHeaders([
    'Accept'       => 'application/json',
    'Content-Type' => 'application/json',
    'X-API-Key'    => $apiKey,
])->post('https://api.mygupta.co/api/sms/send', $payload);

echo $response->successful() ? $response->json()['message_id'] : $response->body();
?>`,

  javascript: `function formatPhoneNumber(phone) {
  phone = phone.replace(/[^0-9]/g, '');
  if (!phone.startsWith('234') && phone.startsWith('0')) phone = '234' + phone.slice(1);
  else if (!phone.startsWith('234')) phone = '234' + phone;
  return phone;
}

const apiKey   = 'YOUR_API_KEY_HERE';
const senderId = 'YOUR_SENDER_ID';

fetch('https://api.mygupta.co/api/sms/send', {
  method: 'POST',
  headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-API-Key': apiKey },
  body: JSON.stringify({
    sender_id: senderId,
    recipients: [formatPhoneNumber('08012345678')],
    message: 'Your OTP is: 123456. Valid for 60 seconds.',
  }),
})
  .then(r => r.json())
  .then(d => console.log(d.status === 'success' ? d.message_id : d.message))
  .catch(console.error);`,

  python: `import requests, json, re

def format_phone(phone):
    phone = re.sub(r'[^0-9]', '', phone)
    if not phone.startswith('234') and phone.startswith('0'):
        phone = '234' + phone[1:]
    elif not phone.startswith('234'):
        phone = '234' + phone
    return phone

api_key   = 'YOUR_API_KEY_HERE'
sender_id = 'YOUR_SENDER_ID'

r = requests.post('https://api.mygupta.co/api/sms/send',
    headers={'Accept': 'application/json', 'Content-Type': 'application/json', 'X-API-Key': api_key},
    data=json.dumps({'sender_id': sender_id, 'recipients': [format_phone('08012345678')],
                     'message': 'Your OTP is: 123456. Valid for 60 seconds.'}))
print(r.json().get('message_id') if r.status_code == 200 else r.text)`,

  curl: `curl -X POST "https://api.mygupta.co/api/sms/send" \\
  -H "Accept: application/json" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY_HERE" \\
  -d '{
    "sender_id": "YOUR_SENDER_ID",
    "recipients": ["2348012345678"],
    "message": "Your OTP is: 123456. Valid for 60 seconds."
  }'`,
};

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "php", label: "PHP" },
  { id: "javascript", label: "JavaScript" },
  { id: "python", label: "Python" },
  { id: "curl", label: "cURL" },
];

export default function ApiKeyManagement() {
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newApiKeyName, setNewApiKeyName] = useState("");
  const [expiryDate, setExpiryDate] = useState<any>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<any>(null);
  const [showKey, setShowKey] = useState<Record<number, boolean>>({});
  const [successOpen, setSuccessOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [devGuideOpen, setDevGuideOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchApiKeys(); }, []);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const res: any = await SmsApis.getApiKeys();
      if (res?.data) {
        setApiKeys(res.data?.data || []);
        const init: Record<number, boolean> = {};
        res.data?.data?.forEach((k: any) => { init[k.id] = false; });
        setShowKey(init);
      } else toast.error("Failed to fetch API keys");
    } catch { toast.error("An error occurred while fetching API keys"); }
    finally { setLoading(false); }
  };

  const copyToClipboard = (text: string, label = "Copied!") => {
    navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(() => toast.error("Failed to copy"));
  };

  const createApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await SmsApis.createApiKey(newApiKeyName, expiryDate ? expiryDate.toISOString().split('T')[0] : '');
      if (res?.data) {
        setNewApiKey(res.data?.data?.key);
        setCreateOpen(false);
        setSuccessOpen(true);
        fetchApiKeys();
      } else toast.error("Failed to create API key");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "An error occurred");
    }
  };

  const toggleApiKey = async (id: any) => {
    try {
      const res = await SmsApis.toggleApiKey(id);
      if (res?.data) { toast.success("API key status updated"); fetchApiKeys(); }
      else toast.error("Failed to update API key status");
    } catch { toast.error("An error occurred"); }
  };

  const deleteApiKey = async () => {
    try {
      const res = await SmsApis.deleteApiKey(selectedApiKey.id);
      if (res?.data) { toast.success("API key deleted"); fetchApiKeys(); setDeleteOpen(false); }
      else toast.error("Failed to delete API key");
    } catch { toast.error("An error occurred"); }
  };

  const formatDate = (d: any) => d ? new Date(d).toLocaleDateString() : 'Never';

  const handleCopyKey = async () => {
    await navigator.clipboard.writeText(newApiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Sidebar title="API Keys" />
      <div className="relative md:ml-64 bg-gray-50 min-h-screen">
        <AdminNavbar title="API Keys Management" />
        <div className="px-3 sm:px-5 pt-6 pb-12">

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">API Keys</h1>
              <p className="text-xs text-gray-400 mt-0.5">Manage your API keys for SMS integration</p>
            </div>
            <div className="flex items-center gap-2.5">
              <button onClick={() => { setActiveTab("overview"); setDevGuideOpen(true); }}
                className="flex items-center gap-2 bg-white border border-gray-200 text-gray-600 text-sm font-bold px-4 py-2.5 rounded-2xl hover:bg-gray-50 active:scale-95 transition-all shadow-sm">
                <FiCode size={14} /> Dev Guide
              </button>
              <button onClick={() => { setNewApiKeyName(""); setExpiryDate(null); setCreateOpen(true); }}
                className="flex items-center gap-2 bg-[#0071BC] text-white text-sm font-bold px-4 py-2.5 rounded-2xl hover:bg-[#005fa0] active:scale-95 transition-all shadow-sm">
                <FiPlus size={14} /> New API Key
              </button>
            </div>
          </div>

          {/* Quick start banner */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3.5 mb-5">
            <FiInfo className="text-blue-400 flex-shrink-0 mt-0.5" size={15} />
            <p className="text-xs text-blue-700 leading-relaxed flex-1">
              <strong>Quick Start:</strong> Create an API key, get your Sender ID from the Sender IDs page, and start sending SMS messages.
            </p>
            <button onClick={() => { setActiveTab("overview"); setDevGuideOpen(true); }}
              className="flex-shrink-0 text-xs font-bold text-[#0071BC] hover:underline whitespace-nowrap">
              View Guide →
            </button>
          </div>

          {/* Table card */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {["Name", "API Key", "Expires", "Status", "Actions"].map(h => (
                      <th key={h} className="px-5 py-3 text-xs font-bold text-gray-400 text-left uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    [...Array(3)].map((_, i) => (
                      <tr key={i}>
                        {[120, 240, 80, 70, 80].map((w, j) => (
                          <td key={j} className="px-5 py-4"><div className="h-3.5 bg-gray-100 rounded-lg animate-pulse" style={{ width: w }} /></td>
                        ))}
                      </tr>
                    ))
                  ) : apiKeys.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-1">
                            <FiKey className="text-gray-300" size={22} />
                          </div>
                          <p className="text-sm font-semibold text-gray-400">No API keys yet</p>
                          <p className="text-xs text-gray-300">Click "New API Key" to create your first key</p>
                        </div>
                      </td>
                    </tr>
                  ) : apiKeys.map((key: any) => (
                    <tr key={key.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-5 py-3.5 font-bold text-gray-800 whitespace-nowrap">{key.name}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <code className="text-xs text-gray-600 bg-gray-50 border border-gray-100 px-2.5 py-1.5 rounded-xl font-mono">
                            {showKey[key.id] ? key.key : key.key?.substring(0, 10) + "••••••••••••"}
                          </code>
                          <button onClick={() => setShowKey(p => ({ ...p, [key.id]: !p[key.id] }))}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
                            {showKey[key.id] ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                          </button>
                          <button onClick={() => copyToClipboard(key.key, "API key copied!")}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#0071BC] hover:bg-blue-50 transition-all">
                            <FiCopy size={13} />
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-gray-500 text-xs whitespace-nowrap">{formatDate(key.expires_at)}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${key.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${key.is_active ? "bg-green-500" : "bg-gray-400"}`} />
                          {key.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <button onClick={() => toggleApiKey(key.id)} title={key.is_active ? "Disable" : "Enable"}
                            className={`w-7 h-7 flex items-center justify-center rounded-xl transition-all ${key.is_active ? "bg-amber-50 text-amber-500 hover:bg-amber-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}>
                            <FiPower size={13} />
                          </button>
                          <button onClick={() => { setSelectedApiKey(key); setDeleteOpen(true); }} title="Delete"
                            className="w-7 h-7 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-all">
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Create API Key Modal ──────────────────────────────────── */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} width="max-w-sm">
        <ModalHeader title="New API Key" subtitle="Generate a key for SMS integration" onClose={() => setCreateOpen(false)} />
        <form onSubmit={createApiKey} className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Key Name <span className="text-red-400">*</span></label>
            <input type="text" required placeholder="e.g. Production, Development"
              value={newApiKeyName} onChange={e => setNewApiKeyName(e.target.value)}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Expiry Date <span className="text-gray-400 font-normal">(Optional)</span></label>
            <DatePicker selected={expiryDate} onChange={(d: any) => setExpiryDate(d)}
              minDate={new Date()} placeholderText="No expiry"
              className={inputCls} />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setCreateOpen(false)}
              className="flex-1 py-3.5 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit"
              className="flex-1 py-3.5 bg-[#0071BC] text-white text-sm font-extrabold rounded-2xl hover:bg-[#005fa0] active:scale-[0.97] transition-all flex items-center justify-center gap-2">
              <FiKey size={14} /> Create Key
            </button>
          </div>
        </form>
      </Modal>

      {/* ── API Key Created Modal ─────────────────────────────────── */}
      <Modal open={successOpen} onClose={() => setSuccessOpen(false)} width="max-w-sm">
        <ModalHeader title="API Key Created" onClose={() => setSuccessOpen(false)} />
        <div className="px-6 py-5">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-4">
            <FiAlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={15} />
            <p className="text-xs text-amber-700 leading-relaxed">Copy your key now — we won't show it again for security reasons.</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-3">
            <p className="text-xs text-gray-400 font-medium mb-2">Your API Key</p>
            <code className="text-xs font-mono text-gray-700 break-all leading-relaxed">{newApiKey}</code>
          </div>
          <button onClick={handleCopyKey}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold transition-all mb-3 ${copied ? "bg-green-500 text-white" : "bg-[#0071BC] text-white hover:bg-[#005fa0] active:scale-[0.97]"}`}>
            {copied ? <><FiCheck size={14} /> Copied!</> : <><FiCopy size={14} /> Copy to Clipboard</>}
          </button>
          <button onClick={() => setSuccessOpen(false)}
            className="w-full py-3 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">
            Done
          </button>
        </div>
      </Modal>

      {/* ── Delete Confirm ────────────────────────────────────────── */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} width="max-w-sm">
        <ModalHeader title="Delete API Key" onClose={() => setDeleteOpen(false)} />
        <div className="px-6 py-5">
          <p className="text-sm text-gray-500 mb-1 leading-relaxed">
            Delete <strong className="text-gray-800">"{selectedApiKey?.name}"</strong>? Any integration using this key will stop working immediately.
          </p>
          <p className="text-xs text-red-500 font-semibold mb-5">This action cannot be undone.</p>
          <div className="flex gap-3">
            <button onClick={() => setDeleteOpen(false)}
              className="flex-1 py-3 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={deleteApiKey}
              className="flex-1 py-3 bg-red-500 text-white text-sm font-extrabold rounded-2xl hover:bg-red-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2">
              <FiTrash2 size={13} /> Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Developer Guide Modal ─────────────────────────────────── */}
      <Modal open={devGuideOpen} onClose={() => setDevGuideOpen(false)} width="max-w-4xl">
        <ModalHeader title="Developer Integration Guide" subtitle="Send SMS from your application using the Gupta API" onClose={() => setDevGuideOpen(false)} />

        {/* Tabs */}
        <div className="px-6 pt-4 flex-shrink-0">
          <div className="flex gap-1 bg-gray-100 rounded-2xl p-1">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all ${activeTab === t.id ? "bg-white text-[#0071BC] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          {activeTab === "overview" && (
            <div className="space-y-5">
              <div className="bg-gray-50 rounded-2xl p-5">
                <h3 className="text-sm font-extrabold text-gray-800 mb-3">Getting Started</h3>
                <ol className="space-y-2 text-sm text-gray-600">
                  {["Create an API key using the New API Key button", "Get your Sender ID from the Sender IDs page", "Format phone numbers correctly (see below)", "Use the API endpoint to send SMS messages"].map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="w-5 h-5 rounded-full bg-[#0071BC] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>

              <div>
                <h3 className="text-sm font-extrabold text-gray-800 mb-2">API Endpoint</h3>
                <div className="flex items-center gap-3 bg-gray-900 rounded-2xl px-4 py-3">
                  <span className="text-xs font-bold text-blue-400 bg-blue-900/30 px-2 py-0.5 rounded-lg">POST</span>
                  <code className="text-sm text-green-400 font-mono">https://api.mygupta.co/api/sms/send</code>
                  <button onClick={() => copyToClipboard("https://api.mygupta.co/api/sms/send", "Endpoint copied!")}
                    className="ml-auto text-gray-500 hover:text-gray-300 transition-colors"><FiCopy size={13} /></button>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-extrabold text-gray-800 mb-2">Required Headers</h3>
                <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                  {[["Accept", "application/json"], ["Content-Type", "application/json"], ["X-API-Key", "YOUR_API_KEY_HERE"]].map(([h, v]) => (
                    <div key={h} className="flex items-center px-4 py-3 border-b border-gray-100 last:border-0 text-sm">
                      <code className="text-[#0071BC] font-mono w-40 flex-shrink-0">{h}</code>
                      <span className="text-gray-500">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-extrabold text-gray-800 mb-2">Request Body</h3>
                <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                  {[["sender_id", "string", "Your approved Sender ID (e.g., SND-xxxxx)"], ["recipients", "array", "Array of phone numbers in 234xxxxxxxxx format"], ["message", "string", "The SMS message content"]].map(([p, t, d]) => (
                    <div key={p} className="flex items-start px-4 py-3 border-b border-gray-100 last:border-0 text-sm gap-3">
                      <code className="text-[#0071BC] font-mono w-28 flex-shrink-0">{p}</code>
                      <span className="text-gray-400 w-14 flex-shrink-0">{t}</span>
                      <span className="text-gray-500">{d}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-extrabold text-gray-800 mb-2">Phone Number Format</h3>
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm">
                  <p className="text-amber-700 mb-3 font-medium">Numbers must be in Nigerian format without "+": <code className="bg-amber-100 px-1.5 py-0.5 rounded-lg">2348012345678</code></p>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="font-bold text-amber-800 mb-1">Accepted inputs:</p>
                      {[["08012345678", "→ 2348012345678"], ["+2348012345678", "→ 2348012345678"], ["8012345678", "→ 2348012345678"]].map(([i, o]) => (
                        <p key={i} className="text-amber-700"><code className="bg-amber-100 px-1 rounded">{i}</code> {o}</p>
                      ))}
                    </div>
                    <div>
                      <p className="font-bold text-amber-800 mb-1">Response:</p>
                      <pre className="text-amber-700 text-[11px] leading-relaxed">{`{\n  "status": "success",\n  "message_id": "msg_xxx",\n  "data": {\n    "sent_count": 1,\n    "failed_count": 0\n  }\n}`}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab !== "overview" && CODE_SNIPPETS[activeTab as keyof typeof CODE_SNIPPETS] && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-gray-800 capitalize">{activeTab === "curl" ? "cURL" : activeTab} Example</h3>
                <button onClick={() => copyToClipboard(CODE_SNIPPETS[activeTab as keyof typeof CODE_SNIPPETS], "Code copied!")}
                  className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold px-3 py-1.5 rounded-xl transition-colors">
                  <FiCopy size={11} /> Copy Code
                </button>
              </div>
              <div className="bg-gray-900 rounded-2xl overflow-x-auto">
                <pre className="text-sm font-mono text-green-400 p-5 whitespace-pre leading-relaxed">{CODE_SNIPPETS[activeTab as keyof typeof CODE_SNIPPETS]}</pre>
              </div>
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3">
                <FiAlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={14} />
                <p className="text-xs text-amber-700 leading-relaxed">
                  Replace <code className="bg-amber-100 px-1 rounded">YOUR_API_KEY_HERE</code> with your actual API key and <code className="bg-amber-100 px-1 rounded">YOUR_SENDER_ID</code> with your approved Sender ID.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <p className="text-xs text-gray-400">Need help? Contact <a href="mailto:hello@mygupta.co" className="text-[#0071BC] font-bold hover:underline">hello@mygupta.co</a></p>
        </div>
      </Modal>

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </>
  );
}
